import { Component, ElementRef, Input, Output, EventEmitter, ViewChild, OnChanges } from '@angular/core';
import { SortUtils } from '../../../../shared/utils/sort-utils';
import { ReportsService } from '../../services/reports.service';
import { DateRangeHolder } from '../../../../shared/domain/date-range-holder.model';
import { ReportRequest } from '../../domain/report/report-request.model';
import { ReportMetric, ReportMetricDetails, ReportMetricsAll } from '../../domain/report/report-metric.enum';
import { ReportGrouping } from '../../domain/report/report-grouping.enum';
import { ReportType } from '../../domain/report/report-type.enum';
import { ReportDataSource } from '../../domain/report/report-data-source.enum';
import { ReportDimension } from '../../domain/report/report-dimension.enum';
import { forkJoin } from 'rxjs';

const Highcharts = require('highcharts/highcharts');

@Component({
  selector: 'cm-bidder-metrics-chart',
  template: `
    <div class="p-2">
      <div #chart class="w-100"></div>
    </div>
  `,
  styles: [`
    /* Set in kitchen-dashboard.component.scss */
  `],
  host: { 'class': 'cm-daily-metrics-chart' }
})
export class BidderMetricsChartComponent implements OnChanges {
   @ViewChild('chart', {static: true}) chartEl: ElementRef;

  @Input()
  metrics: ReportMetric[]; // we use only one metric for know, this is for maybe future needs

  @Input()
  dateRange: DateRangeHolder;

  @Input()
  publisherIds: string[];

  @Output() loading = new EventEmitter();

  reportMetricDetails: ReportMetricDetails[] = []; // we use only one metric for know, this is for maybe future needs

  constructor(private reportsService: ReportsService) {
  }

  ngOnChanges() {
    this.reportMetricDetails = ReportMetricsAll().filter(m => this.metrics.includes(m.key));
    this.loadChartData();
  }

  renderChart(xAxisData, series) {
    const opts: any = {
      chart: {
        zoomType: 'xy',
        type: 'area',
        height: 400,
        animation: false
      },
      title: {
        text: null
      },
      xAxis: {
        categories: xAxisData,
        crosshair: true
      },
      yAxis: { opposite: false, title: { text: this.reportMetricDetails[0].label } },
      tooltip: {
        shared: true,
        animation: false
      },
      series: series
    };

    Highcharts.chart(this.chartEl.nativeElement, opts);
  }

  loadChartData() {
    const reportRequestBidder = this.getCommonReport();
    reportRequestBidder.filters.bidder = true;
    const reportRequestNotBidder = this.getCommonReport();
    reportRequestNotBidder.filters.bidder = false;

    this.loading.emit(true);
    forkJoin([
      this.reportsService.report(reportRequestBidder),
      this.reportsService.report(reportRequestNotBidder)
    ]).subscribe(([resBidder, resNonBidder]) => {
      resBidder.report.sort(SortUtils.propertyComparatorString('day'));
      resNonBidder.report.sort(SortUtils.propertyComparatorString('day'));

      const xAxisGeneralData = resBidder.report.map(element => element.day);

      const series = [];
      series.push(this.getSeries(resNonBidder.report, this.reportMetricDetails[0], 'Non bidder'));
      series.push(this.getSeries(resBidder.report, this.reportMetricDetails[0], 'Bidder'));

      this.renderChart(xAxisGeneralData, series);
      this.loading.emit(false);
    }, error => {
      this.loading.emit(false);
    });
  }

  getSeries(dataArray: any[], metricDetails: ReportMetricDetails, legendName: string) {
    return {
      name: legendName,
      data: this.getData(dataArray, metricDetails),
      tooltip: this.getToolTip(metricDetails)
    };
  }

  getData(dataArray: any[], metricDetails: ReportMetricDetails) {
    switch (metricDetails.key) {
      case ReportMetric.MARGIN:
        return dataArray.map(item => (item[metricDetails.field] || 0) * 100);
      default:
        return dataArray.map(item => item[metricDetails.field]);
    }
  }

  getToolTip(metricDetails: ReportMetricDetails) {
    switch (metricDetails.key) {
      case ReportMetric.REFRESH_RATE:
        return { valueDecimals: 2 };
      case ReportMetric.MARGIN:
        return { valueDecimals: 2, valueSuffix: '%' };
      case ReportMetric.REVENUE:
      case ReportMetric.RPM:
        return { valueDecimals: 2, valuePrefix: '$' };
      default:
        return;
    }
  }

  getCommonReport(): ReportRequest {
    return <ReportRequest>{
      reportGrouping: ReportGrouping.COMBINED,
      reportType: ReportType.FINANCIAL,
      dataSource: ReportDataSource.DATE_PERIOD_DEPENDENT,
      metrics: this.metrics,
      dimensions: [
        ReportDimension.DAY
      ],
      predicates: [
      ],
      filters: {
        dateFrom: this.dateRange.dateFrom,
        dateTo: this.dateRange.dateTo,
        publisherIds: this.publisherIds
      }
    };
  }

}
